import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Session timeout in milliseconds (10 minutes)
const SESSION_TIMEOUT = 10 * 60 * 1000

// Activity events to track
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

export function useDemoMode() {
  const router = useRouter()
  const authStore = useAuthStore()

  const isDemoMode = ref(localStorage.getItem('demo_mode') === 'true')
  const lastActivity = ref(Date.now())
  const timeRemaining = ref(SESSION_TIMEOUT)
  const showTimeoutWarning = ref(false)

  let activityInterval = null
  let countdownInterval = null

  // Check if current user is a demo user
  const isDemoUser = computed(() => {
    const user = authStore.user
    if (!user) return false
    return user.username?.startsWith('demo_') || isDemoMode.value
  })

  // Restricted actions for demo users
  const restrictedActions = [
    'create',
    'edit',
    'delete',
    'update',
    'remove',
    'add',
    'save',
    'submit',
    'publish',
    'unpublish'
  ]

  // Check if an action is allowed
  function isActionAllowed(action) {
    if (!isDemoUser.value) return true
    const actionLower = action.toLowerCase()
    return !restrictedActions.some(restricted => actionLower.includes(restricted))
  }

  // Show demo restriction message
  function showDemoRestriction() {
    // Create toast notification
    const toast = document.createElement('div')
    toast.className = 'fixed bottom-4 right-4 bg-yellow-500 text-black px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 animate-slide-up'
    toast.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <span>Esta accion no esta disponible en modo demo</span>
    `
    document.body.appendChild(toast)

    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  // Update last activity timestamp
  function updateActivity() {
    lastActivity.value = Date.now()
    showTimeoutWarning.value = false
  }

  // Check session timeout
  function checkSessionTimeout() {
    if (!authStore.isAuthenticated) return

    const now = Date.now()
    const elapsed = now - lastActivity.value
    timeRemaining.value = Math.max(0, SESSION_TIMEOUT - elapsed)

    // Show warning when 2 minutes remaining
    if (timeRemaining.value <= 2 * 60 * 1000 && timeRemaining.value > 0) {
      showTimeoutWarning.value = true
    }

    // Logout when timeout reached
    if (elapsed >= SESSION_TIMEOUT) {
      handleSessionTimeout()
    }
  }

  // Handle session timeout
  function handleSessionTimeout() {
    cleanup()
    localStorage.removeItem('demo_mode')
    localStorage.removeItem('demo_login_time')
    authStore.logout()
    router.push({
      name: 'login',
      query: { reason: 'timeout' }
    })
  }

  // Format remaining time
  function formatTimeRemaining() {
    const minutes = Math.floor(timeRemaining.value / 60000)
    const seconds = Math.floor((timeRemaining.value % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // Start session monitoring
  function startSessionMonitoring() {
    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true })
    })

    // Check timeout every second
    activityInterval = setInterval(checkSessionTimeout, 1000)
  }

  // Cleanup listeners
  function cleanup() {
    ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, updateActivity)
    })

    if (activityInterval) {
      clearInterval(activityInterval)
      activityInterval = null
    }

    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
  }

  // Extend session (for warning modal)
  function extendSession() {
    updateActivity()
    showTimeoutWarning.value = false
  }

  // Initialize on mount
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startSessionMonitoring()
    }
  })

  // Watch for auth changes
  watch(() => authStore.isAuthenticated, (isAuth) => {
    if (isAuth) {
      startSessionMonitoring()
    } else {
      cleanup()
    }
  })

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup()
  })

  return {
    isDemoMode,
    isDemoUser,
    timeRemaining,
    showTimeoutWarning,
    isActionAllowed,
    showDemoRestriction,
    formatTimeRemaining,
    extendSession,
    startSessionMonitoring,
    cleanup
  }
}

// Composable for protecting actions
export function useProtectedAction() {
  const { isDemoUser, showDemoRestriction } = useDemoMode()

  function protectedAction(callback, actionName = 'action') {
    return (...args) => {
      if (isDemoUser.value) {
        showDemoRestriction()
        return Promise.resolve({ success: false, error: 'Demo mode restriction' })
      }
      return callback(...args)
    }
  }

  return {
    protectedAction,
    isDemoUser
  }
}
